import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import { useFechaOperativa } from '../../contexts/FechaOperativaContext';
import granjaService, {
  GranjaConfig,
  GranjaInformePosturaSemanal,
} from '../../services/granjaService';
import { getApiErrorMessage } from '../../utils/apiError';
import { toLocalDateStringYMD } from '../../utils/formatters';

const ymdFromApiDate = (value: string): string => {
  const raw = (value || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return toLocalDateStringYMD(d);
};

const formatDateUi = (value: string): string => {
  const ymd = ymdFromApiDate(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return value;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

const formatNum = (n: number, decimals = 0): string =>
  Number(n || 0).toLocaleString('es-AR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const formatPct = (n: number): string =>
  `${Number(n || 0).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

const defaultDesde = (hastaYmd: string): string => {
  const [y, m, d] = hastaYmd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - 84);
  return toLocalDateStringYMD(dt);
};

const GranjaInformes: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { fechaYmd } = useFechaOperativa();
  const diaOperativo = fechaYmd ?? toLocalDateStringYMD();

  const [config, setConfig] = useState<GranjaConfig | null>(null);
  const [desde, setDesde] = useState(() => defaultDesde(diaOperativo));
  const [hasta, setHasta] = useState(diaOperativo);
  const [lotesSel, setLotesSel] = useState<string[]>([]);
  const [informe, setInforme] = useState<GranjaInformePosturaSemanal | null>(null);
  const [loading, setLoading] = useState(false);

  const lotesDisponibles = useMemo(
    () => (config?.lotes ?? []).filter((l) => l.activo && l.codigo.trim()),
    [config],
  );

  useEffect(() => {
    (async () => {
      try {
        const cfg = await granjaService.getConfig();
        setConfig(cfg);
        const activos = (cfg.lotes ?? [])
          .filter((l) => l.activo && l.codigo.trim())
          .map((l) => l.codigo.trim());
        setLotesSel(activos);
      } catch (err: unknown) {
        enqueueSnackbar(getApiErrorMessage(err, 'Error al cargar configuración'), {
          variant: 'error',
        });
      }
    })();
  }, [enqueueSnackbar]);

  const cargar = useCallback(async () => {
    if (!desde || !hasta) {
      enqueueSnackbar('Indicá el rango de fechas', { variant: 'warning' });
      return;
    }
    if (lotesSel.length === 0) {
      enqueueSnackbar('Seleccioná al menos un lote', { variant: 'warning' });
      return;
    }
    try {
      setLoading(true);
      const data = await granjaService.getInformePosturaSemanal({
        desde,
        hasta,
        lotes: lotesSel,
      });
      setInforme(data);
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al generar el informe'), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, lotesSel, enqueueSnackbar]);

  useEffect(() => {
    if (lotesSel.length === 0) return;
    void cargar();
    // Carga inicial al tener lotes; regenerar con el botón.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const toggleLote = (codigo: string) => {
    setLotesSel((prev) =>
      prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo],
    );
  };

  const exportarExcel = () => {
    if (!informe || informe.filas.length === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    const codigos = informe.codigosLote;
    const headerLotes: (string | number)[] = ['', ''];
    const headerCols: (string | number)[] = ['Fecha Inicial', 'Fecha Final'];

    codigos.forEach((cod) => {
      headerLotes.push(cod, '', '', '', '');
      headerCols.push(
        'Cantidad Postura',
        'SEMANAS PRODUCTIVAS',
        'Muertas',
        'Cantidad Gallinas',
        'Porcentaje Postura',
      );
    });
    headerLotes.push('TOTAL', '', '', '');
    headerCols.push(
      'Cantidad Postura',
      'Muertas',
      'Cantidad Gallinas',
      'Porcentaje Postura',
    );

    const rows: (string | number)[][] = [headerLotes, headerCols];

    for (const fila of informe.filas) {
      const row: (string | number)[] = [
        formatDateUi(fila.fechaInicial),
        formatDateUi(fila.fechaFinal),
      ];
      for (const cod of codigos) {
        const celda = fila.lotes.find(
          (l) => l.codigoLote.trim().toUpperCase() === cod.trim().toUpperCase(),
        );
        row.push(
          celda?.cantidadPostura ?? 0,
          celda?.semanasProductivas ?? 0,
          celda?.muertas ?? 0,
          celda?.cantidadGallinas ?? 0,
          (celda?.porcentajePostura ?? 0) / 100,
        );
      }
      row.push(
        fila.total.cantidadPostura,
        fila.total.muertas,
        fila.total.cantidadGallinas,
        fila.total.porcentajePostura / 100,
      );
      rows.push(row);
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Postura semanal');
    XLSX.writeFile(
      wb,
      `granja-postura-semanal-${ymdFromApiDate(informe.desde)}_${ymdFromApiDate(informe.hasta)}.xlsx`,
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            Informes · Granja
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Postura semanal por lote (semana lunes–domingo), con el mismo formato del libro de
            referencia.
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'flex-end' }}
              >
                <TextField
                  label="Desde"
                  type="date"
                  size="small"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 170 }}
                />
                <TextField
                  label="Hasta"
                  type="date"
                  size="small"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 170 }}
                />
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={() => void cargar()}
                  disabled={loading}
                  sx={{ textTransform: 'none' }}
                >
                  Generar
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={exportarExcel}
                  disabled={!informe || informe.filas.length === 0}
                  sx={{ textTransform: 'none' }}
                >
                  Exportar Excel
                </Button>
              </Stack>

              {lotesDisponibles.length === 0 ? (
                <Alert severity="warning">No hay lotes activos configurados.</Alert>
              ) : (
                <FormGroup row>
                  {lotesDisponibles.map((l) => (
                    <FormControlLabel
                      key={l.codigo}
                      control={
                        <Checkbox
                          size="small"
                          checked={lotesSel.includes(l.codigo.trim())}
                          onChange={() => toggleLote(l.codigo.trim())}
                        />
                      }
                      label={l.codigo}
                    />
                  ))}
                </FormGroup>
              )}
            </Stack>
          </CardContent>
        </Card>

        {informe && informe.filas.length === 0 && (
          <Alert severity="info">No hay semanas en el rango seleccionado.</Alert>
        )}

        {informe && informe.filas.length > 0 && (
          <TableContainer
            component={Card}
            sx={{ borderRadius: 2, overflowX: 'auto', maxHeight: '70vh' }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{ bgcolor: '#fff', borderBottom: 0, position: 'sticky', left: 0, zIndex: 3 }}
                  />
                  {informe.codigosLote.map((cod) => (
                    <TableCell
                      key={`h-${cod}`}
                      align="center"
                      colSpan={5}
                      sx={{ bgcolor: '#e3f2fd', fontWeight: 700 }}
                    >
                      {cod}
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    colSpan={4}
                    sx={{ bgcolor: '#fff3e0', fontWeight: 700 }}
                  >
                    TOTAL
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: '#fafafa',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                    }}
                  >
                    Fecha Inicial
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: '#fafafa',
                      position: 'sticky',
                      left: 110,
                      zIndex: 3,
                    }}
                  >
                    Fecha Final
                  </TableCell>
                  {informe.codigosLote.map((cod) => (
                    <React.Fragment key={`c-${cod}`}>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>
                        Cant. Postura
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>
                        Sem. Prod.
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>
                        Muertas
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa' }}>
                        Gallinas
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, bgcolor: '#fafafa', whiteSpace: 'nowrap', minWidth: 72 }}
                      >
                        % Postura
                      </TableCell>
                    </React.Fragment>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fff8e1' }}>
                    Cant. Postura
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fff8e1' }}>
                    Muertas
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#fff8e1' }}>
                    Gallinas
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, bgcolor: '#fff8e1', whiteSpace: 'nowrap', minWidth: 72 }}
                  >
                    % Postura
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {informe.filas.map((fila) => (
                  <TableRow key={`${fila.fechaInicial}-${fila.fechaFinal}`} hover>
                    <TableCell
                      sx={{ position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}
                    >
                      {formatDateUi(fila.fechaInicial)}
                    </TableCell>
                    <TableCell
                      sx={{ position: 'sticky', left: 110, bgcolor: '#fff', zIndex: 1 }}
                    >
                      {formatDateUi(fila.fechaFinal)}
                    </TableCell>
                    {informe.codigosLote.map((cod) => {
                      const celda = fila.lotes.find(
                        (l) =>
                          l.codigoLote.trim().toUpperCase() === cod.trim().toUpperCase(),
                      );
                      return (
                        <React.Fragment key={`${fila.fechaInicial}-${cod}`}>
                          <TableCell align="right">
                            {formatNum(celda?.cantidadPostura ?? 0)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNum(celda?.semanasProductivas ?? 0)}
                          </TableCell>
                          <TableCell align="right">{formatNum(celda?.muertas ?? 0)}</TableCell>
                          <TableCell align="right">
                            {formatNum(celda?.cantidadGallinas ?? 0)}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {formatPct(celda?.porcentajePostura ?? 0)}
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                    <TableCell align="right" sx={{ bgcolor: alphaTotal }}>
                      {formatNum(fila.total.cantidadPostura)}
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: alphaTotal }}>
                      {formatNum(fila.total.muertas)}
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: alphaTotal }}>
                      {formatNum(fila.total.cantidadGallinas)}
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: alphaTotal, whiteSpace: 'nowrap' }}>
                      {formatPct(fila.total.porcentajePostura)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Box>
  );
};

const alphaTotal = 'rgba(255, 243, 224, 0.45)';

export default GranjaInformes;
